import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Typography } from 'antd';
import {
  GoogleOutlined,
  LinkOutlined,
  DisconnectOutlined,
  ReloadOutlined,
  CalendarOutlined,
  LoadingOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SectionShell } from './SectionShell';
import type { CalendarStatus } from '../../../api/providerMeApi';

const { Text } = Typography;

interface CalendarSectionProps {
  /**
   * Backend status: `connected`, `available` (kill switch + plan), `reason`.
   * Render strategiyasi:
   *  - `available === false && reason === 'FEATURE_DISABLED'` → `null`
   *    (foydalanuvchi platform admin kill switch'idan bexabar qoladi)
   *  - `available === false && reason === 'PREMIUM_REQUIRED'` → upgrade banner
   *  - aks holda — odatdagi Ulash / Ulangan card
   */
  status: CalendarStatus;
  connecting: boolean;
  disconnecting: boolean;
  checking: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onCheckStatus: () => void;
}

type ButtonVariant = 'primary' | 'secondary' | 'danger';

export function CalendarSection({
  status,
  connecting,
  disconnecting,
  checking,
  onConnect,
  onDisconnect,
  onCheckStatus,
}: CalendarSectionProps) {
  const { t } = useTranslation('provider');

  // Kill switch — platform admin funksiyani o'chirib qo'ygan. Card umuman
  // renderlanmaydi, foydalanuvchi bu holatdan bexabar qoladi.
  if (!status.available && status.reason === 'FEATURE_DISABLED') {
    return null;
  }

  // PREMIUM kerak — card o'rniga upgrade banner. Provayder o'zi tarifni
  // yangilay olmaydi (bu OWNER huquqi), shuning uchun banner informativ.
  if (!status.available && status.reason === 'PREMIUM_REQUIRED') {
    return (
      <SectionShell
        caption={t('profile.sections.calendarCaption')}
        icon={<CalendarOutlined />}
        iconColor="#ea4335"
      >
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            padding: '12px 14px',
            borderRadius: 12,
            background:
              'linear-gradient(135deg, rgba(250,173,20,0.08) 0%, rgba(250,173,20,0.16) 100%)',
            border: '1px solid rgba(250,173,20,0.22)',
          }}
        >
          <div
            aria-hidden
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(250,173,20,0.18)',
              color: '#d48806',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            <CrownOutlined />
          </div>
          <Text
            style={{
              fontSize: 13,
              color: 'var(--bl-text-strong, #0f172a)',
              lineHeight: 1.45,
              flex: 1,
              minWidth: 0,
            }}
          >
            {t('profile.calendar.premiumRequiredBanner')}
          </Text>
        </div>
      </SectionShell>
    );
  }

  const connected = status.connected;

  const statusPill = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 700,
        padding: '2px 10px',
        borderRadius: 999,
        letterSpacing: 0.2,
        background: connected
          ? 'rgba(31,139,76,0.10)'
          : 'var(--bl-muted-soft, #eef0f3)',
        color: connected ? '#1f8b4c' : 'var(--bl-text-muted, #6b7280)',
        border: connected
          ? '1px solid rgba(31,139,76,0.20)'
          : '1px solid var(--bl-divider, #e5e7eb)',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: connected ? '#1f8b4c' : '#9ca3af',
          boxShadow: connected ? '0 0 0 3px rgba(31,139,76,0.18)' : 'none',
        }}
      />
      {connected
        ? t('profile.calendar.statusConnected')
        : t('profile.calendar.statusDisconnected')}
    </span>
  );

  return (
    <SectionShell
      caption={t('profile.sections.calendarCaption')}
      icon={<CalendarOutlined />}
      iconColor="#ea4335"
      extra={statusPill}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          aria-hidden
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            background: 'linear-gradient(135deg, rgba(234,67,53,0.12) 0%, rgba(234,67,53,0.24) 100%)',
            border: '1px solid rgba(234,67,53,0.18)',
            color: '#ea4335',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          <GoogleOutlined />
        </div>
        <Text
          style={{
            fontSize: 13,
            color: 'var(--bl-text-muted, #6b7280)',
            lineHeight: 1.45,
            flex: 1,
            minWidth: 0,
          }}
        >
          {connected
            ? t('profile.calendar.hintConnected')
            : t('profile.calendar.hintDisconnected')}
        </Text>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 14,
          flexWrap: 'wrap',
        }}
      >
        {connected ? (
          <CalButton
            variant="danger"
            icon={<DisconnectOutlined />}
            label={t('profile.calendar.disconnectBtn')}
            loading={disconnecting}
            onClick={onDisconnect}
            fullWidth
          />
        ) : (
          <>
            <CalButton
              variant="primary"
              icon={<LinkOutlined />}
              label={t('profile.calendar.connectBtn')}
              loading={connecting}
              onClick={onConnect}
            />
            <CalButton
              variant="secondary"
              icon={<ReloadOutlined />}
              label={t('profile.calendar.checkStatusBtn')}
              loading={checking}
              onClick={onCheckStatus}
            />
          </>
        )}
      </div>
    </SectionShell>
  );
}

function CalButton({
  variant,
  icon,
  label,
  loading = false,
  fullWidth = false,
  onClick,
}: {
  variant: ButtonVariant;
  icon: ReactNode;
  label: string;
  loading?: boolean;
  fullWidth?: boolean;
  onClick: () => void;
}) {
  const [pressed, setPressed] = useState(false);

  const variants: Record<ButtonVariant, CSSProperties> = {
    primary: {
      background: 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
      color: '#fff',
      border: '1px solid rgba(22,119,255,0.18)',
      boxShadow:
        '0 4px 12px rgba(22,119,255,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
    },
    secondary: {
      background: 'var(--bl-surface, #fff)',
      color: 'var(--bl-text-strong, #0f172a)',
      border: '1px solid var(--bl-divider, #e5e7eb)',
      boxShadow: 'none',
    },
    danger: {
      background: 'rgba(255,77,79,0.06)',
      color: '#d93b3e',
      border: '1px solid rgba(255,77,79,0.22)',
      boxShadow: 'none',
    },
  };

  return (
    <button
      type="button"
      onClick={() => !loading && onClick()}
      disabled={loading}
      onTouchStart={() => !loading && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => !loading && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        ...variants[variant],
        flex: fullWidth ? '1 1 100%' : '1 1 auto',
        minHeight: 42,
        padding: '0 16px',
        borderRadius: 11,
        fontSize: 13,
        fontWeight: 700,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.85 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        transform: pressed && !loading ? 'scale(0.985)' : 'scale(1)',
        transition: 'transform 0.1s ease, opacity 0.15s ease',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        letterSpacing: 0.1,
        fontFamily: 'inherit',
      }}
    >
      {loading ? <LoadingOutlined spin /> : icon}
      <span>{label}</span>
    </button>
  );
}
